import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, Pencil, ArrowLeft, Phone, Mail, MapPin, FileText,
  Truck, Receipt, TrendingUp, Star, Package, Scissors, Timer, CheckCircle2, Plus,
} from "lucide-react";
import { toast } from "sonner";
import SupplierMaterialPicker from "@/components/SupplierMaterialPicker";
import DefinitionPicker from "@/components/DefinitionPicker";
import NewPurchaseDialog from "@/components/NewPurchaseDialog";
import SupplierPriceLists from "@/components/SupplierPriceLists";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("tr-TR"); } catch { return "—"; } };

export default function SupplierProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [purchaseMaterial, setPurchaseMaterial] = useState(null);

  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get(`/suppliers/${id}/profile`);
      setData(data);
    } catch (e) { setLoadError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const openEdit = () => {
    setForm({
      ...data.supplier,
      fabric_ids: (data.fabrics || []).map((f) => f.id),
      accessory_ids: (data.accessories || []).map((a) => a.id),
    });
    setEditOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/suppliers/${id}`, form);
      toast.success("Tedarikçi güncellendi");
      setEditOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (loadError || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="supplier-profile-error">
        <Truck className="w-10 h-10 text-slate-700" />
        <div className="text-slate-400 text-sm">{loadError || "Tedarikçi bulunamadı"}</div>
        <Button onClick={() => navigate("/tedarikci")} variant="outline" size="sm"
          className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm uppercase tracking-wider text-xs font-semibold"
          data-testid="supplier-profile-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Listeye Dön
        </Button>
      </div>
    );
  }

  const { supplier, stats, purchases, fabrics, accessories } = data;
  const mapsHref = supplier.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(supplier.address)}`
    : null;

  return (
    <div>
      <PageHeader
        title={supplier.name}
        subtitle={supplier.contact_person || "Tedarikçi profili"}
        breadcrumbs={["Modüller", "Tedarikçiler", supplier.name]}
        actions={
          <div className="flex gap-2">
            <Button onClick={() => navigate("/tedarikci")} variant="outline" size="sm" className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm uppercase tracking-wider text-xs font-semibold">
              <ArrowLeft className="w-4 h-4 mr-2" /> Liste
            </Button>
            <Button
              data-testid="new-purchase-from-supplier-btn"
              onClick={() => { setPurchaseMaterial(null); setPurchaseOpen(true); }}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" /> Yeni Alım
            </Button>
            <Button data-testid="edit-supplier-btn" onClick={openEdit} size="sm" className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
              <Pencil className="w-4 h-4 mr-2" /> Düzenle
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <StatCard label="Toplam Alım" value={stats.total_purchases} icon={Receipt} color="text-blue-500" />
          <StatCard label="Harcama (₺)" value={`₺${fmt(stats.total_spent)}`} icon={TrendingUp} color="text-emerald-400" />
          {(stats.avg_unit_price_by_unit || []).length > 1 ? (
            // Birden fazla birim (kg/metre/adet) tek ortalamada birleşmez — birim bazında göster
            <Card className="bg-slate-900 border-slate-800 rounded-sm p-4" data-testid="stat-avg-unit-multi">
              <div className="flex items-center justify-between mb-2">
                <Package className="w-4 h-4 text-blue-400" strokeWidth={2.5} />
              </div>
              <div className="space-y-0.5">
                {stats.avg_unit_price_by_unit.map((u) => (
                  <div key={u.unit} className="text-sm font-bold text-slate-50 tabular-nums font-mono"
                    data-testid={`stat-avg-unit-${u.unit}`}>
                    ₺{fmt(u.avg_unit_price)}<span className="text-slate-500 font-normal">/{u.unit}</span>
                  </div>
                ))}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mt-1">Ort. Birim ₺ (birim bazında)</div>
            </Card>
          ) : (
            <StatCard label="Ort. Birim ₺"
              value={(stats.avg_unit_price_by_unit || []).length === 1
                ? `₺${fmt(stats.avg_unit_price_by_unit[0].avg_unit_price)}/${stats.avg_unit_price_by_unit[0].unit}`
                : "—"}
              icon={Package} color="text-blue-400" />
          )}
          <StatCard label="Ort. Teslim (gün)" value={stats.avg_lead_days} icon={Timer} color="text-slate-300" />
          <StatCard label="Zamanında %" value={`${stats.on_time_rate}%`} icon={CheckCircle2} color={stats.on_time_rate >= 80 ? "text-emerald-400" : "text-yellow-400"} />
          <StatCard label="Puan" value={supplier.rating ? `⭐ ${supplier.rating}` : "—"} icon={Star} color="text-yellow-400" />
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="bg-slate-900 border border-slate-800 rounded-sm h-auto p-1 flex-wrap justify-start">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs uppercase tracking-wider font-semibold px-4 py-2">Genel Bakış</TabsTrigger>
            <TabsTrigger value="purchases" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs uppercase tracking-wider font-semibold px-4 py-2">Alımlar ({purchases.length})</TabsTrigger>
            <TabsTrigger value="materials" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs uppercase tracking-wider font-semibold px-4 py-2">Malzemeler ({fabrics.length + accessories.length})</TabsTrigger>
            <TabsTrigger value="priceLists" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs uppercase tracking-wider font-semibold px-4 py-2" data-testid="tab-price-lists">Fiyat Listeleri</TabsTrigger>
            <TabsTrigger value="contact" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs uppercase tracking-wider font-semibold px-4 py-2">İletişim</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="md:col-span-2 bg-slate-900 border-slate-800 rounded-sm p-5">
                <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">Firma</h3>
                <div className="space-y-2 text-sm">
                  <InfoRow icon={Truck} label="Firma" value={supplier.name} />
                  <InfoRow icon={FileText} label="Vergi No" value={supplier.tax_no || "—"} mono />
                  <InfoRow icon={MapPin} label="Adres" value={supplier.address || "—"} />
                </div>
                {supplier.brand_names?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-800" data-testid="supplier-profile-brands">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Ürün Markaları</div>
                    <div className="flex flex-wrap gap-1.5">
                      {supplier.brand_names.map((t, i) => (
                        <span key={i} className="text-[11px] px-2 py-0.5 border border-emerald-900/60 text-emerald-300 bg-emerald-950/30 rounded-sm font-mono">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
                {supplier.material_tags?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-800">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Malzeme Etiketleri</div>
                    <div className="flex flex-wrap gap-1.5">
                      {supplier.material_tags.map((t, i) => (
                        <span key={i} className="text-[11px] px-2 py-0.5 border border-blue-900/60 text-blue-300 bg-blue-950/30 rounded-sm font-mono">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
              <Card className="bg-slate-900 border-slate-800 rounded-sm p-5">
                <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">Son Alım</h3>
                <div className="text-sm text-slate-200">{stats.last_purchase_date ? fmtDate(stats.last_purchase_date) : "—"}</div>
                {mapsHref && (
                  <a href={mapsHref} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-mono text-blue-500 hover:text-blue-400">
                    <MapPin className="w-3.5 h-3.5" /> Google Maps'te Aç
                  </a>
                )}
                {supplier.notes && (
                  <div className="mt-4 pt-4 border-t border-slate-800">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">Not</div>
                    <div className="text-xs text-slate-300 whitespace-pre-wrap">{supplier.notes}</div>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="purchases" className="mt-4 space-y-3">
            <div className="flex justify-end">
              <Button
                data-testid="purchases-tab-new-btn"
                onClick={() => { setPurchaseMaterial(null); setPurchaseOpen(true); }}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" /> Bu Tedarikçiden Yeni Alım
              </Button>
            </div>
            <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
              {purchases.length === 0 ? (
                <div className="text-center py-10 px-4 text-slate-500 text-sm space-y-3">
                  <Receipt className="w-10 h-10 mx-auto text-slate-700" />
                  <div>Bu tedarikçiden alım yok.</div>
                  <Button
                    onClick={() => { setPurchaseMaterial(null); setPurchaseOpen(true); }}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
                  >
                    <Plus className="w-4 h-4 mr-2" /> İlk Alımı Oluştur
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-slate-800">
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Alım No</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tarih</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Malzeme</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Miktar</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Birim ₺</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Toplam ₺</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Fatura</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {purchases.map((p) => (
                      <TableRow key={p.id} className="border-slate-800 hover:bg-slate-800/40">
                        <TableCell className="font-mono text-xs text-blue-500 font-semibold">{p.purchase_no}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-400">{fmtDate(p.purchase_date)}</TableCell>
                        <TableCell className="text-slate-100 text-xs">
                          <div>{p.material_name}</div>
                          <div className="text-[10px] font-mono text-slate-500">{p.material_code}</div>
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-xs text-slate-300">{fmt(p.quantity)} <span className="text-slate-500">{p.unit}</span></TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-xs text-slate-300">₺{fmt(p.unit_price)}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-xs text-slate-100 font-semibold">₺{fmt(p.total_price)}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">{p.invoice_no || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="materials" className="mt-4 space-y-3">
            {fabrics.length > 0 && (
              <Card className="bg-slate-900 border-slate-800 rounded-sm p-5">
                <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
                  <Scissors className="w-3.5 h-3.5" /> Kumaşlar ({fabrics.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {fabrics.map((f) => (
                    <span key={f.id} className="group inline-flex items-center gap-2 text-xs px-2.5 py-1 border border-slate-700 bg-slate-800/60 rounded-sm text-slate-200 font-mono">
                      <span className="text-blue-500">{f.code}</span>
                      <span>· {f.name}</span>
                      <button
                        type="button"
                        data-testid={`quick-buy-fabric-${f.id}`}
                        onClick={() => { setPurchaseMaterial({ type: "fabric", id: f.id, code: f.code, name: f.name }); setPurchaseOpen(true); }}
                        title={`${f.name} için hızlı alım oluştur`}
                        className="ml-1 opacity-70 group-hover:opacity-100 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-950/40 rounded-sm p-0.5 transition-colors"
                      >
                        <Plus className="w-3 h-3" strokeWidth={3} />
                      </button>
                    </span>
                  ))}
                </div>
              </Card>
            )}
            {accessories.length > 0 && (
              <Card className="bg-slate-900 border-slate-800 rounded-sm p-5">
                <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
                  <Package className="w-3.5 h-3.5" /> Aksesuarlar ({accessories.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {accessories.map((a) => (
                    <span key={a.id} className="group inline-flex items-center gap-2 text-xs px-2.5 py-1 border border-slate-700 bg-slate-800/60 rounded-sm text-slate-200 font-mono">
                      <span className="text-blue-500">{a.code}</span>
                      <span>· {a.name}</span>
                      <button
                        type="button"
                        data-testid={`quick-buy-accessory-${a.id}`}
                        onClick={() => { setPurchaseMaterial({ type: "accessory", id: a.id, code: a.code, name: a.name }); setPurchaseOpen(true); }}
                        title={`${a.name} için hızlı alım oluştur`}
                        className="ml-1 opacity-70 group-hover:opacity-100 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-950/40 rounded-sm p-0.5 transition-colors"
                      >
                        <Plus className="w-3 h-3" strokeWidth={3} />
                      </button>
                    </span>
                  ))}
                </div>
              </Card>
            )}
            {fabrics.length === 0 && accessories.length === 0 && (
              <Card className="bg-slate-900 border-slate-800 rounded-sm p-10 text-center space-y-3">
                <Package className="w-10 h-10 mx-auto text-slate-700" />
                <div className="text-slate-500 text-sm">
                  Bu tedarikçiye bağlı malzeme yok. Önce "Düzenle" ile kumaş/aksesuar ekleyin, sonra alım yapabilirsiniz.
                </div>
                <Button onClick={openEdit} size="sm" variant="outline" className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm uppercase tracking-wider text-xs font-semibold">
                  <Pencil className="w-4 h-4 mr-2" /> Malzeme Ekle
                </Button>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="contact" className="mt-4">
            <Card className="bg-slate-900 border-slate-800 rounded-sm p-5 space-y-3">
              <InfoRow icon={Phone} label="Telefon" value={supplier.phone || "—"} mono />
              <InfoRow icon={Mail} label="E-posta" value={supplier.email || "—"} />
              <InfoRow icon={MapPin} label="Adres" value={supplier.address || "—"} />
              <InfoRow icon={FileText} label="Vergi No" value={supplier.tax_no || "—"} mono />
            </Card>
          </TabsContent>
          <TabsContent value="priceLists" className="mt-4">
            <SupplierPriceLists supplierId={id} />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-50 tracking-tight">Tedarikçi Düzenle</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Firma Adı</Label>
                <Input data-testid="edit-supplier-name-input" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Yetkili</Label>
                <Input value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Telefon</Label>
                <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">E-posta</Label>
                <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Vergi No</Label>
                <Input value={form.tax_no || ""} onChange={(e) => setForm({ ...form, tax_no: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Puan (0-5)</Label>
                <Input type="number" step="0.1" min="0" max="5" value={form.rating ?? 0} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Adres</Label>
                <Textarea rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 resize-none" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Notlar</Label>
                <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 resize-none" />
              </div>
              <div className="col-span-2 pt-2 mt-1 border-t border-slate-800">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">
                  Bu tedarikçiden temin edilen malzemeler
                </div>
              </div>
              <div className="col-span-2">
                <SupplierMaterialPicker
                  kind="fabric"
                  value={form.fabric_ids || []}
                  onChange={(ids) => setForm({ ...form, fabric_ids: ids })}
                  testIdPrefix="edit-supplier-fabric-picker"
                />
              </div>
              <div className="col-span-2">
                <SupplierMaterialPicker
                  kind="accessory"
                  value={form.accessory_ids || []}
                  onChange={(ids) => setForm({ ...form, accessory_ids: ids })}
                  testIdPrefix="edit-supplier-accessory-picker"
                />
              </div>
              <div className="col-span-2">
                <DefinitionPicker
                  kind="brand"
                  sourceUrl="/suppliers/brand-options"
                  label="Ürün Markaları"
                  value={form.brand_names || []}
                  onChange={(names) => setForm({ ...form, brand_names: names })}
                  testIdPrefix="edit-supplier-brand-picker"
                  placeholder="Marka ara…"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">İptal</Button>
              <Button data-testid="save-supplier-edit-btn" type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <NewPurchaseDialog
        open={purchaseOpen}
        onOpenChange={setPurchaseOpen}
        supplierId={supplier.id}
        supplierName={supplier.name}
        initialMaterial={purchaseMaterial}
        onSuccess={load}
      />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color = "text-slate-300" }) {
  return (
    <Card className="bg-slate-900 border-slate-800 rounded-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <Icon className={`w-4 h-4 ${color}`} strokeWidth={2.5} />
      </div>
      <div className="text-lg font-bold text-slate-50 tabular-nums font-mono truncate">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mt-1">{label}</div>
    </Card>
  );
}

function InfoRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
        <div className={`text-sm text-slate-100 ${mono ? "font-mono" : ""} break-words`}>{value}</div>
      </div>
    </div>
  );
}
